import useSimpleDialog from '@app/hooks/use-simple-dialog';

import { useDeleteOperationReport } from '@shared/api/hooks/reports/reports';
import {
  CompanyReportStatusEnum,
  OperationReportList,
} from '@shared/api/types';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import { Button } from '@shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { MoreHorizontal } from 'lucide-react';
import { useCallback, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { ReportType } from '../types';
import {
  getReportsPathMap,
  ReportFilters,
  useReportFilterParams,
} from '../utils';

interface ReportCardActionsProps {
  report: OperationReportList;
  reportType: ReportType;
}

function DeleteDialogContent({
  report,
  reportType,
  closeDialog,
}: {
  report: OperationReportList;
  closeDialog: () => void;
  reportType: ReportType;
}) {
  const { toast } = useToast();
  const { mutateAsync: deleteReport, isLoading: isDeleting } =
    useDeleteOperationReport();

  const handleDelete = useCallback(async () => {
    try {
      await deleteReport({ operationReportUuid: report.uuid });
      closeDialog();
      toast({
        title: 'Report deleted',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete report',
        description: 'Please refresh and try again',
        variant: 'destructive',
      });
    }
  }, [deleteReport, report.uuid, closeDialog, toast]);

  return (
    <div className="space-y-6">
      <Text>
        Deleting this report will not delete any related {reportType} data. This
        action cannot be undone.
      </Text>
      <div className="mt-2 flex justify-end space-x-2">
        <div className="col-span-4">
          <Button
            type="button"
            size="lg"
            variant="secondary"
            onClick={closeDialog}
            disabled={isDeleting}
          >
            Cancel
          </Button>
        </div>
        <div className="col-span-4">
          <Button
            type="submit"
            size="lg"
            disabled={isDeleting}
            onClick={handleDelete}
            variant="destructive"
          >
            Delete report
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ReportCardActions({
  report,
  reportType,
}: ReportCardActionsProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const {
    showSimpleDialog: showDeleteDialog,
    SimpleDialogComponent: DeleteDialog,
    closeSimpleDialog: closeDeleteDialog,
  } = useSimpleDialog();
  const navigate = useNavigate();
  const disabled = report.status === CompanyReportStatusEnum.publishing;

  const filterParams = useReportFilterParams(
    report?.filters as ReportFilters[]
  );

  const purchasedFoodItemsLink = `${
    getReportsPathMap()[reportType].foodData
  }?${filterParams.toString()}`;

  const purchasedPackagingItemsLink = `${
    getReportsPathMap()[reportType].packagingData
  }?${filterParams.toString()}`;

  const editLink = generatePath(getReportsPathMap()[reportType].edit!, {
    report_uuid: report.uuid,
  });
  const reportLink = generatePath(getReportsPathMap()[reportType].report, {
    report_uuid: report.uuid,
  });

  return (
    <div className="flex items-center justify-end space-x-2">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <Button variant="ghost" size="sm">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-50 w-[200px]">
          <DropdownMenuLabel className="text-muted-foreground">
            View {reportType} data
          </DropdownMenuLabel>
          <DropdownMenuGroup
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {report.includesFood && (
              <DropdownMenuItem
                onClick={() => navigate(purchasedFoodItemsLink)}
              >
                <Icons.carrot className="size-4" />
                Food and drink
              </DropdownMenuItem>
            )}
            {report.includesPackaging && (
              <DropdownMenuItem
                onClick={() => navigate(purchasedPackagingItemsLink)}
              >
                <Icons.package className="size-4" />
                Packaging
              </DropdownMenuItem>
            )}
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={showDeleteDialog}
            className="cursor-default rounded-sm font-normal text-destructive focus:bg-destructive-foreground focus:text-destructive"
          >
            Delete report
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button
        variant="outline"
        onClick={() => navigate(editLink)}
        disabled={disabled}
      >
        Edit
      </Button>
      <Button
        className="bg-teal"
        onClick={() => navigate(reportLink)}
        disabled={disabled}
        data-testid="view-report-button"
      >
        View report
      </Button>
      {DeleteDialog({
        header: 'Delete report',
        contentClassName: 'max-w-lg',
        content: (
          <DeleteDialogContent
            report={report}
            reportType={reportType}
            closeDialog={closeDeleteDialog}
          />
        ),
      })}
    </div>
  );
}
